<template>
  <div class="tooltip-container" @mouseenter="showTooltip" @mouseleave="hideTooltip">
    <button
      :disabled="!isButtonEnabled"
      class="tw-p-3 tw-cursor-pointer tw-bg-white tw-shadow-sm tw-rounded-lg tw-text-[#F1583D] tw-flex tw-items-center tw-font-medium tw-whitespace-nowrap tw-border tw-border-solid tw-border-[#DFE2E7] disabled:tw-bg-[#EEEFF2] disabled:tw-text-[#B3B9C6]"
      @click="optimizeAssetLocation"
    >
      <i class="fas fa-arrow-down tw-mr-2" />
      <span v-if="locationloading">
        <i class="fa fa-spinner fa-spin tw-text-xl tw-mr-1" />
        Optimizing Asset Location
      </span>
      <span v-else>Optimize Asset Location</span>
    </button>
    <div v-if="tooltipVisible" class="tooltip">
      <p>
        {{ tooltipMessage }}
        <br />
        For more details, check our
        <a
          href="https://help.yourstake.org/knowledge/asset-location-distribution-in-advisor-core"
          target="_blank"
          class="tooltip-link"
        >
          help article
        </a>
      </p>
    </div>
  </div>
</template>

<script>
import { useCheckupStore } from '@frontend/ui/stores/checkupStore';
import { mapStores } from 'pinia';

export default {
  data() {
    return {
      locationloading: false,
      tooltipVisible: false, // Tooltip visibility state
    };
  },
  computed: {
    ...mapStores(useCheckupStore),
    isButtonEnabled() {
      return this.checkEnabled().isEnabled;
    },
    tooltipMessage() {
      return this.checkEnabled().message;
    },
  },
  methods: {
    showTooltip() {
      this.tooltipVisible = true;
    },
    hideTooltip() {
      this.tooltipVisible = false;
    },
    checkEnabled() {
      if (this.checkupStore.selectedPortfolios.length === 0) {
        return {
          isEnabled: false,
          message: "Please select at least one portfolio in the 'Current Portfolios' section.",
        };
      }
      const holdings = this.checkupStore.holdingsFromPortfolios.map(holding => ({
        accountTaxStatus: this.getAcctTaxStatus(holding),
      }));

      const hasClassifiedStatus = holdings.some(
        holding => holding.accountTaxStatus && holding.accountTaxStatus !== 'Unclassified'
      );

      if (!hasClassifiedStatus) {
        return {
          isEnabled: false,
          message: "Current portfolios are missing tax status. You can edit this on the holdings tab.",
        };
      }

      return {
        isEnabled: true,
        message: 'Allocates holdings from proposed portfolio into tax status buckets from current portfolios.',
      };
    },
    getPortfolioField(holding, field) {
      if (typeof holding === 'object' && holding.ticker !== null) {
        return holding[field];
      }
      return null;
    },
    getAcctTaxStatus(holding) {
      const acct_tax_status = this.getPortfolioField(holding, 'account_tax_status_string');
      if (acct_tax_status) {
        return acct_tax_status;
      }
      // Unmatched holdings have attributes stored in uploaded_df
      return holding.account_tax_status;
    },
    calculateTaxabilityPercents() {
      const totalDollars = this.checkupStore.holdingsFromPortfolios.reduce((sum, holding) => sum + holding.dollars, 0);

      let taxableDollars = 0;
      let taxDeferredDollars = 0;
      let taxFreeDollars = 0;

      for (const holding of this.checkupStore.holdingsFromPortfolios) {
        const accountTaxStatus = this.getAcctTaxStatus(holding);

        // Assuming taxable for unclassified for now
        if (accountTaxStatus === 'Unclassified') {
          taxableDollars += holding.dollars;
        } else if (accountTaxStatus === '') {
          taxableDollars += holding.dollars;
        } else if (accountTaxStatus === 'Taxable') {
          taxableDollars += holding.dollars;
        } else if (accountTaxStatus === 'Tax Deferred') {
          taxDeferredDollars += holding.dollars;
        } else if (accountTaxStatus === 'Tax Free') {
          taxFreeDollars += holding.dollars;
        }
      }

      return {
        taxablePercent: totalDollars > 0 ? taxableDollars / totalDollars : 0,
        taxDeferredPercent: totalDollars > 0 ? taxDeferredDollars / totalDollars : 0,
        taxFreePercent: totalDollars > 0 ? taxFreeDollars / totalDollars : 0,
      };
    },
    async optimizeAssetLocation() {
      this.locationloading = true; // Activate spinner
      try {
        const baseUrl = '/api/v1/distribute_portfolio_across_account_taxability/';
        const url = new URL(baseUrl, window.location.origin);

        const { taxablePercent, taxDeferredPercent, taxFreePercent } = this.calculateTaxabilityPercents();

        url.searchParams.append('external_id',this.checkupStore.optimizedPortfolio.external_id);
        url.searchParams.append('taxable_percent', taxablePercent);
        url.searchParams.append('tax_deferred_percent', taxDeferredPercent);
        url.searchParams.append('tax_free_percent', taxFreePercent);
        url.searchParams.append(
          'total_dollars',
          this.checkupStore.holdingsFromPortfolios.reduce((sum, holding) => sum + holding.dollars, 0)
        );

        const response = await fetch(url);
        if (!response.ok) {
          const errorData = await response.json();
          console.error('Error optimizing asset location:', errorData.error || 'Unknown error');
          return;
        }

        const data = await response.json();

        // Update optimizedPortfolio
        const optimizedPortfolio = data.optimal_location_portfolio;
        this.checkupStore.removePortfolio(this.checkupStore.optimizedPortfolio);
        this.checkupStore.addPortfolio(optimizedPortfolio, 'optimizedPortfolio');

        // Download the CSV
        const csvBlob = new Blob([data.csv], { type: 'text/csv' });
        const downloadUrl = window.URL.createObjectURL(csvBlob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.setAttribute('download', 'optimized_portfolio.csv');
        document.body.appendChild(link);
        link.click();
        link.remove();
      } catch (error) {
        console.error('Error in optimizeAssetLocation:', error);
      } finally {
        this.locationloading = false; // Deactivate spinner
      }
    },
  },
};
</script>


<style scoped>
.tooltip-container {
  position: relative;
  display: inline-block;
}

.tooltip {
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  background: #fff;
  border: 1px solid #dfe2e7;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  padding: 10px;
  z-index: 1000;
  width: 300px;
  border-radius: 5px;
}

.tooltip-link {
  color: #f1583d;
  text-decoration: underline;
}
</style>
